import React, { useEffect, useState, useContext } from 'react';
import UserContext, { getUserDisplayName } from "../contexts/user.js";
import StateContext from '../contexts/state.js';
import getQueryOverride from '../helpers/queryOverride.js';
import useUser from '../hooks/useUser.js';
import { getUserData } from '../helpers/api/getUserData.js';
import { changeBrowserTitle } from '../hooks/headAmender.js';

const accountDefailFields = [
    { label: "Email", key: "email" },
    { label: "Country", key: "country" },
    { label: "Locality", key: "locality" },
    { label: "Line of service", key: "LOS" },
    { label: "Grade", key: "pwcGlobalGradeName" },
    // { label: "Department", key: "upwcdepartment" },
    { label: "Local LoS 1", key: "pwcLocalLOSLevel1" },
    { label: "Local LoS 2", key: "pwcLocalLOSLevel2" },
    { label: "Local LoS 3", key: "pwcLocalLOSLevel3" }
];

/* Where the effective LoS/location values come from, matching UserConfig's order: query, override snippet, SSO */
const valueSource = ({ queryKey, overrideValue, overrideMode }) => {
    if(getQueryOverride(queryKey) !== null) {
        return "testing override";
    }
    if([ overrideValue ].flat().filter(Boolean).length) {
        return overrideMode === "replace" ? "set by an administrator" : "SSO + added by an administrator";
    }
    return "SSO";
};

const UserPage = () => {
    const { userContext } = useContext(UserContext);
    const { state } = useContext(StateContext);
    const { logout } = useUser();

    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);

    const translateNotificationStatus = status => {
        if(status === "granted") {
            return "granted";
        }
        if(status === "denied") {
            return "denied"
        }
        return "default";
    }

    const [notificationsStatus, setNotificationStatus] = useState(translateNotificationStatus());

    useEffect(() => {
        let isMounted = true;
        const loadUserData = async () => {
            try {
                const data = await getUserData({ logout });
                isMounted && setUserData(data);
            }
            catch (error) {
                console.error(error);
            }
            isMounted && setLoading(false);
        };
        loadUserData();
        return () => isMounted = false;
    }, []);

    changeBrowserTitle("Your account");

    /* Listen to notification changes */
    navigator?.permissions?.query({ name: "notifications" })?.then((status) => {
        /* Initial state */
        setNotificationStatus(translateNotificationStatus(status.state));

        /* Listen for changes */
        status.onchange = () => {
            setNotificationStatus(translateNotificationStatus(status.state));
        };
    });

    /* Re-request notifications */
    const askForNotifications = () => {
        Notification.requestPermission();
    };

    return (
        <section>
            <h1>Hello { getUserDisplayName(userContext, { includeSurname: true }) }</h1>

            <p>Find your user account details below:</p>
            { loading ? "Loading ..." : (
                <ul style={{
                    listStyleType: "none",
                    padding: "20px",
                    background: "#fff"
                }}>
                    { accountDefailFields
                        .filter(({ key }) => userContext.user?.[key])
                        .map(({ label, key }) => (
                            <li key={key}>{label}: &quot;{userContext.user[key]}&quot;</li>
                        ))
                    }
                </ul>
            )}

            <h2>Used for your content</h2>

            <ul style={{
                listStyleType: "none",
                padding: "20px",
                background: "#fff"
            }}>
                <li>
                    Line of service: { state.userLos?.map(({ value }) => value).join(", ") || "none" } ({
                        valueSource({ queryKey: "los", overrideValue: userContext.override?.los, overrideMode: userContext.override?.losMode })
                    })
                </li>
                <li>
                    Location: { state.userLocation?.map(({ value }) => value).join(", ") || "none" } ({
                        valueSource({ queryKey: "location", overrideValue: userContext.override?.location, overrideMode: userContext.override?.locationMode })
                    })
                </li>
            </ul>

            <h2>Aimtell notifications status</h2>

            <p>
                <strong>
                    {
                        notificationsStatus === "default" ?
                            `You have not set access to the notifications yet.` :
                            `Currently, you have ${ notificationsStatus } the use of notifications.`
                    }
                </strong>
            </p>

            {
                notificationsStatus === "default" && (
                    <button onClick={askForNotifications}>Request notification access</button>
                )
            }

            {
                notificationsStatus === "denied" && (
                    <>
                        <p>To enable receiving news, you&apos;ll need to adjust your notifications settings manually.<br/>
                        You can do so under the page badge icon in the url bar of your web browser.</p>

                        <video
                            autoPlay
                            muted
                            loop
                            style={{
                                width: "100%",
                                height: "auto",
                                aspectRatio: "16 / 9"
                            }}
                        >
                            <source src={`${process.env.PUBLIC_URL}/push-notifications/enable-notifications.mp4`} type="video/mp4" />
                            <img alt="How to enable notifications" src={`${process.env.PUBLIC_URL}/push-notifications/enable-notifications.gif`} />
                        </video>
                    </>
                )
            }
        </section>
    );
};

export default UserPage;
